import {
  formatShengenDefaultFeeLineDisplay,
  SHENGEN_DEFAULT_FEE_LINE_LABELS,
  SHENGEN_DEFAULT_FEE_LINE_ORDER,
  SHENGEN_DEFAULT_STATIC_ADMISSION_PARAGRAPH,
  type ShengenDefaultFeeLineKey,
  type ShengenDefaultFeeVisibility,
} from "@/src/constants/AdmissionOffice/Shengen/shengenDefaultAdmissionConfig";
import { parseMaltaMoneyValue } from "@/src/utils/admissionOffice/malta/buildMaltaFullTimeAdmissionBody";
import { resolveIntakeAcademicYearLabel } from "@/src/utils/admissionOffice/malta/resolveIntakeAcademicYear";

const paragraph = (text: string) => `<p>${text}</p>`;
const strongParagraph = (text: string) => `<p><strong>${text}</strong></p>`;
const spacer = () => `<p>&nbsp;</p>`;

const buildFeeLine = (key: ShengenDefaultFeeLineKey, numericValue: string) => {
  const label = SHENGEN_DEFAULT_FEE_LINE_LABELS[key];
  const displayValue = formatShengenDefaultFeeLineDisplay(key, numericValue);
  if (!displayValue) return "";
  return strongParagraph(`${label}: ${displayValue}`);
};

const buildTransferRequestParagraph = (completeOneYear?: string) => {
  const amount = completeOneYear?.trim();
  if (!amount) return "";
  return `<p style="text-align: center;"><span style="text-decoration: underline; color: #83580c;"><strong>You are requested to transfer the complete first year fee in the amount of ${amount} EUR into the bank account mentioned below:</strong></span></p>`;
};

const buildBankDetailsSection = () =>
  [
    strongParagraph("Beneficiary: INTERNATIONAL EUROPEAN UNIVERSITY"),
    strongParagraph("IBAN: LT293340009929354263"),
    strongParagraph("SWIFT/BIC: UAUPLT2V"),
    strongParagraph(
      "Bank/Payment institution address: Žygimanto Liauksmino g. 3A-4, LT-01101 Vilnius, Republic of Lithuania",
    ),
  ].join("\n");

export interface BuildShengenDefaultAdmissionBodyParams {
  intake?: string;
  feeValues: Pick<Record<ShengenDefaultFeeLineKey, string | undefined>, ShengenDefaultFeeLineKey>;
  feeVisibility: ShengenDefaultFeeVisibility;
  completeOneYear?: string;
}

export const calcShengenDefaultFirstYearTotal = (
  feeValues: Pick<Record<ShengenDefaultFeeLineKey, string | undefined>, ShengenDefaultFeeLineKey>,
  feeVisibility: ShengenDefaultFeeVisibility,
) =>
  SHENGEN_DEFAULT_FEE_LINE_ORDER.reduce((total, key) => {
    if (!feeVisibility[key]) return total;
    return total + parseMaltaMoneyValue(feeValues[key]);
  }, 0);

export const buildShengenDefaultAdmissionBody = ({
  intake,
  feeValues,
  feeVisibility,
  completeOneYear,
}: BuildShengenDefaultAdmissionBodyParams): string => {
  const academicYear = resolveIntakeAcademicYearLabel(intake);
  const parts: string[] = [];

  parts.push(
    paragraph(
      `This letter is to confirm the admission of above-named student in the course mentioned above for the ${academicYear} academic year.`,
    ),
  );
  parts.push(spacer());
  parts.push(paragraph("The fees are as following:"));

  SHENGEN_DEFAULT_FEE_LINE_ORDER.forEach((key) => {
    if (!feeVisibility[key]) return;
    const value = feeValues[key]?.trim();
    if (!value) return;
    const feeLine = buildFeeLine(key, value);
    if (feeLine) {
      parts.push(feeLine);
    }
  });

  parts.push(spacer());

  const transferParagraph = buildTransferRequestParagraph(completeOneYear);
  if (transferParagraph) {
    parts.push(transferParagraph);
    parts.push(spacer());
  }

  parts.push(buildBankDetailsSection());
  parts.push(spacer());
  parts.push(paragraph(SHENGEN_DEFAULT_STATIC_ADMISSION_PARAGRAPH));

  return parts.join("\n");
};
